import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Check, Sparkles, Plus, Lock, 
  Cpu, Rocket, Target, AlertCircle, Loader, Globe,
  Camera, Trash2
} from 'lucide-react';
import API_URL from '../../config';
import '../../pages/events/Events.css';
import '../../pages/events/EventsPrototype.css';

const TECH_INTERESTS_LIST = [
  'Artificial Intelligence', 'Machine Learning', 'Computer Vision', 'Generative AI',
  'Web Development', 'Mobile Apps', 'Cloud Computing', 'Cybersecurity & CTF',
  'Data Science', 'Blockchain & Web3', 'IoT & Embedded', 'Robotics & Automation',
  'Competitive Programming', 'System Design', 'DevOps', 'UI/UX Design', 'Startups'
];

const SUGGESTED_SKILLS = [
  'Python', 'JavaScript', 'TypeScript', 'React', 'Node.js', 'Next.js',
  'C++', 'Java', 'Flutter', 'TensorFlow', 'PyTorch', 'FastAPI',
  'PostgreSQL', 'MongoDB', 'Docker', 'AWS', 'Figma', 'Git'
];

const HOBBY_OPTIONS = [
  'Gaming', 'Photography', 'Music', 'Chess', 'Football', 'Cricket',
  'Badminton', 'Fitness & Gym', 'Reading', 'Travel', 'Anime', 'Cooking',
  'Podcasts', 'Film & Cinema'
];

const NETWORKING_INTENT_OPTIONS = [
  'Hackathon Teammates',
  'Project Collaborators',
  'Technical Networking',
  'Startup-minded People',
  'People with Similar Interests',
  'Faculty / Senior Mentors',
  'Alumni Connections',
  'Future Co-founders'
];

export default function ExternalProfileOnboardingModal({ 
  isOpen, 
  onClose, 
  user, 
  onComplete, 
  eventSlug = '', 
  eventName
}) {
  const [step, setStep] = useState(1); // 1, 2, 3
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [skillInput, setSkillInput] = useState('');

  // Profile Photo state
  const [profilePic, setProfilePic] = useState(() => {
    if (user?.profile_pic) return user.profile_pic;
    try {
      const stored = JSON.parse(localStorage.getItem('convergence_external_user') || '{}');
      return stored.profile_pic || '';
    } catch {
      return '';
    }
  });
  const [isPhotoBusy, setIsPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please choose a valid image file (PNG, JPG, WebP).');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Please choose an image below 2 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setProfilePic(dataUrl);
      setError(null);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemovePhoto = () => {
    setProfilePic('');
  };

  // Form fields
  const [name, setName] = useState(user?.name || '');
  const [rollNo, setRollNo] = useState('');
  const [college, setCollege] = useState(user?.college || '');
  const [collegeEmail, setCollegeEmail] = useState(user?.college_email || '');
  const [branch, setBranch] = useState(user?.branch || '');
  const [year, setYear] = useState(user?.year || '2nd year');
  const [location, setLocation] = useState('');
  const [contact, setContact] = useState(user?.contact || user?.phone || '');
  const [schoolName, setSchoolName] = useState(user?.school_name || '');
  const [juniorCollege, setJuniorCollege] = useState(user?.junior_college || '');
  const [juniorCollegeLocation, setJuniorCollegeLocation] = useState(user?.junior_college_location || '');
  const [bio, setBio] = useState('');
  const [hobbies, setHobbies] = useState([]);
  const [technicalInterests, setTechnicalInterests] = useState([]);
  const [skills, setSkills] = useState([]);
  const [networkingIntent, setNetworkingIntent] = useState([]);

  // Fetch and pre-populate saved profile so user never has to re-enter their information
  useEffect(() => {
    if (!isOpen) return;

    const parseArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [String(parsed)];
      } catch {
        return String(val).split(',').map(s => s.trim()).filter(Boolean);
      }
    };

    const populate = (data) => {
      if (!data) return;
      if (data.profile_pic) setProfilePic(data.profile_pic);
      if (data.name) setName(data.name);
      if (data.roll_no || data.rollNo) setRollNo(data.roll_no || data.rollNo);
      if (data.college) setCollege(data.college);
      if (data.college_email) setCollegeEmail(data.college_email);
      if (data.branch || data.department) setBranch(data.branch || data.department);
      if (data.year) setYear(data.year);
      if (data.location || data.current_locality) setLocation(data.location || data.current_locality);
      if (data.contact || data.phone) setContact(data.contact || data.phone);
      if (data.school_name) setSchoolName(data.school_name);
      if (data.junior_college) setJuniorCollege(data.junior_college);
      if (data.junior_college_location) setJuniorCollegeLocation(data.junior_college_location);
      if (data.bio || data.event_bio) setBio(data.bio || data.event_bio);

      const hobb = parseArray(data.hobbies);
      if (hobb.length > 0) setHobbies(hobb);

      const techInt = parseArray(data.technical_interests || data.interestsTechnical);
      if (techInt.length > 0) setTechnicalInterests(techInt);

      const sk = parseArray(data.skills);
      if (sk.length > 0) setSkills(sk);

      const netInt = parseArray(data.networking_intent);
      if (netInt.length > 0) setNetworkingIntent(netInt);
    };

    if (user) populate(user);
    try {
      const stored = JSON.parse(localStorage.getItem('convergence_external_user'));
      if (stored) populate(stored);
    } catch {}

    const token = localStorage.getItem('convergence_external_token');
    if (token) {
      fetch(`${API_URL}/events-external-auth/session?event_slug=${eventSlug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data && data.profile) populate(data.profile);
        else if (data && data.user) populate(data.user);
      })
      .catch(() => {});
    }
  }, [isOpen, user, eventSlug]);

  if (!isOpen) return null;

  const toggleItem = (list, setList, item) => {
    if (list.includes(item)) {
      setList(list.filter(x => x !== item));
    } else {
      setList([...list, item]);
    }
  };

  const handleAddSkill = (val) => {
    const trimmed = (val || skillInput).trim();
    if (trimmed && !skills.map(s => s.toLowerCase()).includes(trimmed.toLowerCase())) {
      setSkills([...skills, trimmed]);
    }
    setSkillInput('');
  };

  const handleRemoveSkill = (skillToRemove) => {
    setSkills(skills.filter(s => s !== skillToRemove));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please provide your full name.');
      setStep(1);
      return;
    }
    if (!college.trim()) {
      setError('Please provide your college/institution name.');
      setStep(1);
      return;
    }
    if (!branch.trim()) {
      setError('Please provide your branch/department.');
      setStep(1);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const storedExternal = JSON.parse(localStorage.getItem('convergence_external_user') || '{}');
      const activeEmail = user?.email || user?.login_email || storedExternal.email || storedExternal.login_email;
      if (!activeEmail) {
        setError('No active session found. Please verify your email again.');
        setSaving(false);
        return;
      }

      const token = localStorage.getItem('convergence_external_token');
      const payload = {
        event_slug: eventSlug,
        user_email: activeEmail,
        email: activeEmail,
        profile_pic: profilePic,
        name: name.trim(),
        roll_no: rollNo.trim(),
        rollNo: rollNo.trim(),
        college: college.trim(),
        college_email: collegeEmail.trim() || null,
        branch: branch.trim(),
        department: branch.trim(),
        year,
        location: location.trim(),
        current_locality: location.trim(),
        contact: contact.trim(),
        phone: contact.trim(),
        school_name: schoolName.trim(),
        junior_college: juniorCollege.trim(),
        junior_college_location: juniorCollegeLocation.trim(),
        bio: bio.trim(),
        event_bio: bio.trim(),
        hobbies: Array.isArray(hobbies) ? hobbies : [hobbies],
        technical_interests: technicalInterests,
        interestsTechnical: JSON.stringify(technicalInterests),
        skills: Array.isArray(skills) ? skills : [skills],
        networking_intent: networkingIntent
      };

      const res = await fetch(`${API_URL}/events-external-auth/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to save profile.');
        setSaving(false);
        return;
      }

      // Also ensure it is synced in events-networking database
      await fetch(`${API_URL}/events-networking/${eventSlug}/profile/save-onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          event_slug: eventSlug,
          user_email: activeEmail,
          is_external: 1
        })
      }).catch(() => {});

      const updatedUser = {
        ...storedExternal,
        ...(data.user || {}),
        profile_pic: profilePic || data.user?.profile_pic || storedExternal.profile_pic,
        name: name.trim(),
        college: college.trim(),
        branch: branch.trim(),
        year,
        profile_completed: true,
        exists: true
      };

      localStorage.setItem('convergence_external_user', JSON.stringify(updatedUser));
      localStorage.setItem(`vjlink_profile_completed_${eventSlug}_${activeEmail}`, 'true');

      if (onComplete) onComplete(updatedUser);
      onClose();
    } catch (err) {
      console.error(err);
      setError('A network error occurred while saving your profile.');
    } finally {
      setSaving(false);
    }
  };

  const titles = [
    'Let people get to know you.',
    'What are you excited about?',
    'Who do you want to meet?'
  ];

  const copies = [
    `Welcome external attendee! Introduce yourself to participants at ${eventName || 'this event'}.`,
    "Your skills tell people what you bring. Your interests help you find collaborators you'll enjoy building with.",
    "Choose what you want out of this event. We'll use it to make recommendations more relevant."
  ];

  return (
    <div className="vp-modal-backdrop open" onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div className="vp-modal" role="dialog" aria-modal="true" aria-labelledby="ext-wizard-title">
        {/* Modal Head */}
        <div className="vp-modal-head">
          <div>
            <div className="vp-eyebrow">
              <Globe size={11} style={{ display: 'inline', marginRight: 4 }} />
              {eventName ? `${eventName} · Guest Access` : 'External Participant'} · Step {step} of 3
            </div>
            <h2 id="ext-wizard-title">{titles[step - 1]}</h2>
          </div>
          <button className="vp-icon-btn" onClick={onClose} aria-label="Close modal">
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="vp-modal-body">
          {/* Stepper Indicator */}
          <div className="vp-stepper">
            <span className={step >= 1 ? 'done' : ''} />
            <span className={step >= 2 ? 'done' : ''} />
            <span className={step >= 3 ? 'done' : ''} />
          </div>

          <p className="vp-step-copy">{copies[step - 1]}</p>

          {error && (
            <div style={{
              background: '#fff0ee',
              border: '1px solid #ffd2cd',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#a53b31',
              fontSize: '13px',
              marginBottom: '18px'
            }}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* STEP 1: Personal & College Details */}
          {step === 1 && (
            <div className="vp-form-grid">
              {/* Profile Photo Uploader */}
              <div className="vp-field full" style={{
                background: '#f9f9f6',
                border: '1px solid #e8e8e2',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '4px'
              }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#171a24', marginBottom: '8px', display: 'block' }}>
                  Profile Photo
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: '74px',
                      height: '74px',
                      borderRadius: '50%',
                      background: profilePic ? '#eef2ff' : '#0f3d2e',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '26px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      position: 'relative',
                      border: '2px solid #0f3d2e',
                      flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                    title="Click to choose a photo"
                  >
                    {profilePic ? (
                      <img src={profilePic} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span>{(name || user?.name || '?').charAt(0).toUpperCase()}</span>
                    )}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'rgba(0,0,0,0.5)',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Camera size={12} color="#fff" />
                    </div>
                  </div>

                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <input 
                      ref={fileInputRef} 
                      type="file" 
                      accept="image/*" 
                      style={{ display: 'none' }} 
                      onChange={handlePhotoSelect} 
                    />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button 
                        type="button" 
                        className="vp-btn vp-btn-lime vp-btn-small" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isPhotoBusy}
                      >
                        <Camera size={14} /> {profilePic ? 'Change photo' : 'Upload photo'}
                      </button>
                      {profilePic && (
                        <button 
                          type="button" 
                          className="vp-btn vp-btn-outline vp-btn-small" 
                          onClick={handleRemovePhoto}
                          disabled={isPhotoBusy}
                          style={{ color: '#a53b31', borderColor: '#ffd2cd' }}
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      )}
                      {isPhotoBusy && <Loader size={14} className="vp-spin" />}
                    </div>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '6px 0 0', lineHeight: 1.4 }}>
                      Displays on your event networking card and recommendations. (Max 2MB)
                    </p>
                  </div>
                </div>
              </div>

              <div className="vp-field">
                <label>Your full name</label>
                <input 
                  value={name} 
                  onChange={e => setName(e.target.value)} 
                  placeholder="e.g. Rahul Mehta" 
                  required 
                />
              </div>

              <div className="vp-field">
                <label>College / Institution name</label>
                <input 
                  value={college} 
                  onChange={e => setCollege(e.target.value)} 
                  placeholder="e.g. IIIT Hyderabad / CBIT / JNTU" 
                  required 
                />
              </div>

              <div className="vp-field">
                <label>Branch / Domain</label>
                <input 
                  value={branch} 
                  onChange={e => setBranch(e.target.value)} 
                  placeholder="e.g. Computer Science, AI, ECE" 
                  required 
                />
              </div>

              <div className="vp-field">
                <label>Year of study</label>
                <select value={year} onChange={e => setYear(e.target.value)}>
                  <option value="1st year">1st year</option>
                  <option value="2nd year">2nd year</option>
                  <option value="3rd year">3rd year</option>
                  <option value="4th year">4th year</option>
                  <option value="Graduate">Graduate / Other</option>
                </select>
              </div>

              <div className="vp-field">
                <label>Location / City</label>
                <input 
                  value={location} 
                  onChange={e => setLocation(e.target.value)} 
                  placeholder="e.g. Hyderabad, Bengaluru, etc." 
                />
              </div>

              <div className="vp-field">
                <label>WhatsApp / Mobile <span style={{ color: 'var(--vp-muted)', fontWeight: 500 }}>(Private until connected)</span></label>
                <input 
                  type="tel" 
                  value={contact} 
                  onChange={e => setContact(e.target.value)} 
                  placeholder="e.g. 9876543210" 
                />
              </div>

              <div className="vp-field full">
                <label>A little about you</label>
                <textarea 
                  value={bio} 
                  onChange={e => setBio(e.target.value)} 
                  placeholder="What are you learning, building, or curious about? What brings you to this symposium?" 
                />
              </div>
            </div>
          )}

          {/* STEP 2: Skills & Tech Interests */}
          {step === 2 && (
            <div>
              {/* Technical Domains */}
              <div className="vp-field">
                <label>What are you excited about? Choose a few.</label>
                <div className="vp-choice-group" style={{ marginTop: '6px' }}>
                  {TECH_INTERESTS_LIST.map(topic => {
                    const isSelected = technicalInterests.includes(topic);
                    return (
                      <button 
                        key={topic} 
                        type="button" 
                        className={`vp-choice ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleItem(technicalInterests, setTechnicalInterests, topic)}
                      >
                        {isSelected ? '✓ ' : '＋ '}{topic}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Skills Tags Input */}
              <div className="vp-field" style={{ marginTop: '22px' }}>
                <label>Skills & technologies you bring</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    value={skillInput} 
                    onChange={e => setSkillInput(e.target.value)} 
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSkill(); } }}
                    placeholder="Type a skill and press Enter or Add (e.g. React, Python)..." 
                  />
                  <button type="button" className="vp-btn vp-btn-dark vp-btn-small" onClick={() => handleAddSkill()}>
                    <Plus size={14} /> Add
                  </button>
                </div>

                {/* Selected Skills Chips */}
                {skills.length > 0 && (
                  <div className="vp-tag-row" style={{ marginTop: '10px' }}>
                    {skills.map(s => (
                      <span key={s} className="vp-skill-tag" style={{ background: '#eef0ff', color: 'var(--vp-ink)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        {s}
                        <X size={12} style={{ cursor: 'pointer' }} onClick={() => handleRemoveSkill(s)} />
                      </span>
                    ))}
                  </div>
                )}

                {/* Suggested Skills */}
                <div style={{ marginTop: '12px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--vp-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Quick suggestions:
                  </span>
                  <div className="vp-choice-group" style={{ marginTop: '6px' }}>
                    {SUGGESTED_SKILLS.filter(s => !skills.includes(s)).slice(0, 10).map(s => (
                      <button 
                        key={s} 
                        type="button" 
                        className="vp-choice" 
                        style={{ padding: '6px 10px', fontSize: '11px' }}
                        onClick={() => handleAddSkill(s)}
                      >
                        ＋ {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Hobbies / Outside Tech */}
              <div className="vp-field" style={{ marginTop: '22px' }}>
                <label>Outside the technical world</label>
                <div className="vp-choice-group" style={{ marginTop: '6px' }}>
                  {HOBBY_OPTIONS.map(hobby => {
                    const isSelected = hobbies.includes(hobby);
                    return (
                      <button 
                        key={hobby} 
                        type="button" 
                        className={`vp-choice ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleItem(hobbies, setHobbies, hobby)}
                      >
                        {isSelected ? '✓ ' : '＋ '}{hobby}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Goals & Networking Intent */}
          {step === 3 && (
            <div>
              <div className="vp-field">
                <label>What would you like to find here?</label>
                <div className="vp-choice-group" style={{ marginTop: '6px' }}>
                  {NETWORKING_INTENT_OPTIONS.map(intent => {
                    const isSelected = networkingIntent.includes(intent);
                    return (
                      <button 
                        key={intent} 
                        type="button" 
                        className={`vp-choice ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleItem(networkingIntent, setNetworkingIntent, intent)}
                      >
                        {isSelected ? '✓ ' : '＋ '}{intent}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Privacy Guarantee Panel */}
              <div style={{ 
                marginTop: '24px', 
                background: '#f5f8e9', 
                border: '1px solid #e3eccd', 
                borderRadius: '14px', 
                padding: '16px' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontWeight: 800, fontSize: '13px', color: '#273414' }}>
                  <Lock size={15} /> Your profile, your choice.
                </div>
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#485637', lineHeight: 1.5 }}>
                  Your phone number and private contact details remain protected and are never exposed publicly. They become visible only when both you and another attendee mutually accept a connection request.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Foot */}
        <div className="vp-modal-foot">
          {step > 1 ? (
            <button 
              type="button" 
              className="vp-btn vp-btn-outline" 
              onClick={() => setStep(s => s - 1)}
            >
              ← Back
            </button>
          ) : (
            <button 
              type="button" 
              className="vp-btn vp-btn-soft" 
              onClick={onClose}
            >
              Skip for now
            </button>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            {step < 3 ? (
              <button 
                type="button" 
                className="vp-btn vp-btn-dark" 
                onClick={() => setStep(s => s + 1)}
              >
                Continue →
              </button>
            ) : (
              <button 
                type="button" 
                className="vp-btn vp-btn-lime" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader size={14} className="vp-spin" /> Saving profile...
                  </>
                ) : (
                  'Save my profile ✓'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
